import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import api from '../api';
import HackathonSections from '../components/HackathonSections';
import '../styles/section.css';

// Instagram SVG icon component
const InstagramIcon = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
  </svg>
);

const Landing = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({ total: 0, confirmed: 0 });
  const [registrationOpen, setRegistrationOpen] = useState(true);
  const [statusLoaded, setStatusLoaded] = useState(false);
  const [zoomImg, setZoomImg] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get('/teams/count').catch(() => ({ data: { data: { total: 0, confirmed: 0 } } })),
      api.get('/settings/registration-status').catch(() => ({ data: { data: { пријавеОтворене: true } } }))
    ]).then(([statsRes, statusRes]) => {
      setStats(statsRes.data.data);
      setRegistrationOpen(statusRes.data.data.пријавеОтворене);
      setStatusLoaded(true);
    });
  }, []);

  const handleNavigate = (path) => {
    if (!registrationOpen) return;
    navigate(path);
  };

  return (
    <div className="landing">
      {/* ===== HEADER ===== */}
      <motion.header
        className="site-header"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <div className="header-logo" onClick={() => { navigate('/'); window.scrollTo(0, 0); }}>
          <img
            src="https://res.cloudinary.com/dbadhjapn/image/upload/v1772670157/hakatonbezpoz_jadwcr.png"
            alt="ХакатонКМ"
            className="header-logo-img"
          />
          <span className="header-logo-text">ХакатонКМ</span>
        </div>

        <div className="header-buttons">
          {registrationOpen ? (
            <>
              <button className="header-btn btn-studenti" onClick={() => navigate('/prijava/studenti')}>
                🎒 Пријави се као студент
              </button>
              <button className="header-btn btn-ucenici" onClick={() => navigate('/prijava/ucenici')}>
                🎓 Пријави се као ученик
              </button>
            </>
          ) : statusLoaded ? (
            <div className="header-closed-badge">
              🔒 Пријаве су затворене
            </div>
          ) : null}
        </div>
      </motion.header>

      {/* Logo strip */}
      <motion.div
        className="logo-strip"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
      >
        {[
          { src: 'https://res.cloudinary.com/dnx6y0737/image/upload/v1772574166/ftnkm_wnd24z.png', alt: 'ФТН КМ', link: 'https://ftn.pr.ac.rs/' },
          { src: 'https://res.cloudinary.com/dbadhjapn/image/upload/v1774040463/Flashback_to_the_Future_13_ebihvt.png', alt: 'Гимназија КМ', link: 'https://gimnazijakm.edu.rs/' },
          { src: 'https://res.cloudinary.com/dbadhjapn/image/upload/v1774029502/Flashback_to_the_Future_7_vvrcyc.png', alt: 'ПМФ КМ', link: 'https://pmf.pr.ac.rs/' },
        ].map((logo, i) => (
          <motion.div
            key={i}
            className={`logo-placeholder${i === 1 ? ' center-logo' : ''}`}
            initial={{ scale: 0 }}
            animate={
              i === 1
                ? { scale: [0.8, 1.15, 1], y: [10, 0] }
                : { scale: 1 }
            }
            transition={
              i === 1
                ? { delay: 0.05, type: "spring", stiffness: 450, damping: 14 }
                : { delay: 0.45 + (i === 0 ? 0 : 0.12), type: "spring" }
            }
          >
            <a href={logo.link} target="_blank" rel="noopener noreferrer">
              <img src={logo.src} alt={logo.alt} />
            </a>
          </motion.div>
        ))}
      </motion.div>

      {/* Main hero */}
      <section
        className={registrationOpen ? 'hero-split' : undefined}
        style={!registrationOpen ? {
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '20px 24px 60px',
          maxWidth: '1200px',
          margin: '0 auto',
          width: '100%'
        } : undefined}
      >
        <motion.div
          className="hero-left"
          initial={{ x: -40, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.6 }}
          style={!registrationOpen ? { textAlign: 'center', maxWidth: '640px' } : undefined}
        >
          <h1 className="hero-title">ХакатонКМ</h1>
          <h2 className="hero-subtitle">Такмичење за младе иновативне идеје</h2>
          <p className="hero-description" style={!registrationOpen ? { margin: '0 auto 24px' } : undefined}>
            Агенда се налази у наставку. Хвала свима на огромном интересовању!
          </p>

          <div className="hero-badges" style={!registrationOpen ? { justifyContent: 'center' } : undefined}>
            <span className="badge"><span className="badge-icon">🏆</span> Вредне награде</span>
            <span className="badge"><span className="badge-icon">👥</span> Тимски рад</span>
            <span className="badge"><span className="badge-icon">💡</span> Иновативне идеје</span>
          </div>
        </motion.div>

        {registrationOpen ? (
          <div className="hero-right">
            <div className="hero-date-badge">
              <span>📅</span> 3. и 4. април 2026.
            </div>
            <motion.div
              className="category-card ucenici"
              onClick={() => handleNavigate('/prijava/ucenici')}
              initial={{ x: 50, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.6, duration: 0.5 }}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              <div className="category-icon">🎓</div>
              <div className="category-text">
                <h3 className="category-title">Ученици</h3>
                <p className="category-desc">За ученике основних и средњих школа</p>
              </div>
              <div className="category-arrow">→</div>
            </motion.div>

            <motion.div
              className="category-card studenti"
              onClick={() => handleNavigate('/prijava/studenti')}
              initial={{ x: 50, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.75, duration: 0.5 }}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              <div className="category-icon">🎒</div>
              <div className="category-text">
                <h3 className="category-title">Студенти</h3>
                <p className="category-desc">За студенте факултета и високих школа</p>
              </div>
              <div className="category-arrow">→</div>
            </motion.div>
          </div>
        ) : statusLoaded ? (
          <>
          <div className="agenda-section">
                <motion.div
                  className="agenda-card"
                  onClick={() => setZoomImg('https://res.cloudinary.com/dbadhjapn/image/upload/v1775149529/agenda1_bj7rvf.jpg')}
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.6, duration: 0.5 }}
                >
                  <img src="https://res.cloudinary.com/dbadhjapn/image/upload/v1775149529/agenda1_bj7rvf.jpg" alt="Агенда - Дан 1" />
                  <div className="agenda-label">📅 Дан 1 — Агенда</div>
                </motion.div>

                <motion.div
                  className="agenda-card"
                  onClick={() => setZoomImg('https://res.cloudinary.com/dbadhjapn/image/upload/v1775149529/agenda2_pvmaoo.jpg')}
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.7, duration: 0.5 }}
                >
                  <img src="https://res.cloudinary.com/dbadhjapn/image/upload/v1775149529/agenda2_pvmaoo.jpg" alt="Агенда - Дан 2" />
                  <div className="agenda-label">📅 Дан 2 — Агенда</div>
                </motion.div>
              </div>
            <motion.div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '24px',
                width: '100%',
                maxWidth: '1000px',
                margin: '0 auto',
                padding: '0 24px'
              }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
            >
              {/* Closed badge */}
              

              {/* Agenda */}
              
              <div className="registration-closed-card" style={{ maxWidth: '480px' }}>
                <div className="closed-icon">🔒</div>
                <h3 className="closed-title">Пријаве су затворене</h3>
                <p className="closed-text">
                  Хвала свим учесницима на огромном интересовању!
                  Пријаве за ХакатонКМ такмичење су завршене.
                </p>
                <p className="closed-subtext">
                  Пратите наше канале за додатне информације о такмичењу.
                </p>
              </div>
            </motion.div>

            {/* Zoom overlay */}
            {zoomImg && (
              <div className="agenda-overlay" onClick={() => setZoomImg(null)}>
                <button className="agenda-overlay-close" onClick={() => setZoomImg(null)}>✕</button>
                <img src={zoomImg} alt="Агенда" />
              </div>
            )}
          </>
        ) : null}
      </section>

      {/* Hackathon info sections */}
      <HackathonSections />

      {/* Footer */}
      <footer className="site-footer" id="sponzori">
        <div className="footer-sponsors">
          <span className="footer-sponsors-label">Спонзори</span>
          <div className="sponsor-cards">
            {[
              {
                name: 'MS Techs',
                img: 'https://res.cloudinary.com/dbadhjapn/image/upload/w_300,h_300,c_fill,g_center/v1773683262/Untitled_design_6_n9tdez.png',
                ig: 'https://www.instagram.com/mstechs.cloud?igsh=MWU5MjE3dHBnZmVkcg=='
              },
              {
                name: 'Zimi Ti',
                img: 'https://res.cloudinary.com/dbadhjapn/image/upload/w_300,h_300,c_fill,g_center/v1773683011/Untitled_design_4_h4ji9b.png',
                ig: 'https://www.instagram.com/zimi_ti?igsh=MTJzYWc3ejh2bmhrcw=='
              },
              {
                name: 'Канцеларија за КиМ',
                img: 'https://res.cloudinary.com/dbadhjapn/image/upload/w_300,h_300,c_fill,g_center/v1773949512/Flashback_to_the_Future_3_adfbbt.png',
                ig: 'https://www.instagram.com/samojedno?igsh=Y213NDl2NGFpaXV1'
              },
              {
                name: 'Студентски центар',
                img: 'https://res.cloudinary.com/dbadhjapn/image/upload/w_300,h_300,c_fill,g_center/v1773949640/Flashback_to_the_Future_5_bmlmyt.png',
                ig: 'https://scp.org.rs/'
              },
              {
                name: 'W3 Lab',
                img: 'https://res.cloudinary.com/dbadhjapn/image/upload/w_300,h_300,c_fill,g_center/v1773949511/Flashback_to_the_Future_4_ibzrnd.png',
                ig: 'https://www.instagram.com/www3lab?igsh=MTd0Nmlpbnh4N3B5Zw=='
              }
            ].map((s, i) => (
              <motion.a
                key={i}
                href={s.ig}
                target="_blank"
                rel="noopener noreferrer"
                className="sponsor-circle"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="sponsor-img-wrap">
                  <img src={s.img} alt={s.name} />
                </div>
                <span className="sponsor-circle-name">{s.name}</span>
              </motion.a>
            ))}
          </div>
        </div>

        <div className="footer-bottom">
          <div className="footer-grid footer-grid-2col">
            {/* Contact */}
            <div>
              <div className="footer-col-title">Контакт</div>
              <div className="footer-contact-list">
                <a href="tel:+381669819240" className="footer-contact-item">
                  <span>📱</span> +381 66 981 9240
                </a>
                <a href="mailto:info@mstechs.cloud" className="footer-contact-item">
                  <span>📧</span> info@mstechs.cloud
                </a>
              </div>
            </div>

            {/* Social */}
            <div>
              <div className="footer-col-title">Пратите нас</div>
              <div className="footer-social-list footer-social-row-icons">
                <a
                  href="https://www.instagram.com/hakatonkm?igsh=a2N4em8yNTYzamRo"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="footer-icon-link ig"
                  title="@hakatonkm"
                >
                  <InstagramIcon />
                </a>
                <a
                  href="https://mstechs.cloud"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="footer-icon-link web"
                  title="mstechs.cloud"
                >
                  🌐
                </a>
              </div>
            </div>
          </div>

          <div className="footer-divider" />

          <div className="footer-bar">
            <div className="footer-powered">
              <span className="footer-powered-label">Powered by</span>
              <a href="https://mstechs.cloud" target="_blank" rel="noopener noreferrer" className="footer-powered-link">
                <span className="pw-icon">⚡</span>
                <span className="pw-name">mstechs<span className="pw-tld">.cloud</span></span>
              </a>
            </div>
            <div className="footer-copy">© {new Date().getFullYear()} ХакатонКМ — Сва права задржана</div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;